import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, Loader2, Users } from 'lucide-react';
import { Button } from '~/components/ui/button';
import { Checkbox } from '~/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import { Tabs, TabsList, TabsTrigger } from '~/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '~/components/ui/table';
import { formatCurrency } from '~/utils/paymentUtils';
import { useBulkInvoicePreview, useCreateBulkInvoices } from './useBulkInvoices';

export type BulkInvoiceTarget = 'FAMILIES' | 'STUDENTS';

const PREVIEW_ROW_LIMIT = 10;
const SKIPPED_ROW_LIMIT = 5;
// Past this many invoices the write takes seconds rather than an instant, so the
// dialog says so instead of looking stuck.
const SLOW_WRITE_INVOICES = 500;

export interface BulkInvoiceFamily {
  /** Composite family enrollment id. */
  id: string;
  name: string;
  studentCount: number;
}

export interface BulkInvoiceStudent {
  personId: string;
  name: string;
  familyName: string;
  gradeLevel?: string | null;
}

interface BulkInvoiceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  schoolYearId: string | undefined;
  families: readonly BulkInvoiceFamily[];
  students: readonly BulkInvoiceStudent[];
  /**
   * Where the drafts are posted from: the school-wide Invoices page. When given,
   * the completion notice links there.
   */
  invoicesPath?: string;
  onCompleted: (message: ReactNode) => void;
}

function dollarsToCents(value: string) {
  const parsed = parseFloat(value || '0');
  if (!Number.isFinite(parsed)) return NaN;
  return Math.round(parsed * 100);
}

// A one-off charge is due soon; a run of installments starts next month, on the
// day the admin picks.
function defaultDueOn(installmentCount: number) {
  const date = new Date();
  if (installmentCount > 1) {
    date.setMonth(date.getMonth() + 1);
    date.setDate(1);
  } else {
    date.setDate(date.getDate() + 1);
  }
  return date.toISOString().slice(0, 10);
}

export function BulkInvoiceDialog({
  open,
  onOpenChange,
  schoolYearId,
  families,
  students,
  invoicesPath,
  onCompleted,
}: BulkInvoiceDialogProps) {
  const [target, setTarget] = useState<BulkInvoiceTarget>('STUDENTS');
  // Each tab keeps its own picks, so switching to check what a family looks like
  // does not throw away a list of students already chosen.
  const [studentIds, setStudentIds] = useState<string[]>([]);
  const [familyIds, setFamilyIds] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [installmentCount, setInstallmentCount] = useState('1');
  const [firstDueOn, setFirstDueOn] = useState(() => defaultDueOn(1));
  const [error, setError] = useState<string | null>(null);

  const [runPreview, { data: previewData, loading: previewing }] = useBulkInvoicePreview();
  const [createBulkInvoices, { loading: creating }] = useCreateBulkInvoices();

  useEffect(() => {
    if (!open) return;
    setTarget('STUDENTS');
    setStudentIds([]);
    setFamilyIds([]);
    setSearch('');
    setDescription('');
    setAmount('');
    setInstallmentCount('1');
    setFirstDueOn(defaultDueOn(1));
    setError(null);
  }, [open]);

  const byStudent = target === 'STUDENTS';
  const ids = byStudent ? studentIds : familyIds;
  const setIds = byStudent ? setStudentIds : setFamilyIds;

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (byStudent) {
      return students
        .filter((student) =>
          !needle ||
          student.name.toLowerCase().includes(needle) ||
          student.familyName.toLowerCase().includes(needle)
        )
        .map((student) => ({
          id: student.personId,
          title: student.name,
          detail: [student.familyName, student.gradeLevel].filter(Boolean).join(' · '),
        }));
    }
    return families
      .filter((family) => !needle || family.name.toLowerCase().includes(needle))
      .map((family) => ({
        id: family.id,
        title: family.name,
        detail: `${family.studentCount} ${family.studentCount === 1 ? 'student' : 'students'}`,
      }));
  }, [byStudent, students, families, search]);

  const allVisibleSelected = visible.length > 0 && visible.every((row) => ids.includes(row.id));

  const toggleRow = (id: string, checked: boolean) => {
    setIds((current) => (checked ? [...current, id] : current.filter((value) => value !== id)));
  };

  const toggleAllVisible = () => {
    const visibleIds = visible.map((row) => row.id);
    setIds((current) =>
      allVisibleSelected
        ? current.filter((id) => !visibleIds.includes(id))
        : Array.from(new Set([...current, ...visibleIds]))
    );
  };

  const amountCents = dollarsToCents(amount);
  const parsedInstallments = parseInt(installmentCount, 10);

  const inputError = useMemo(() => {
    if (ids.length === 0) return `Select at least one ${byStudent ? 'student' : 'family'}.`;
    if (!description.trim()) return 'Say what the charge is for.';
    if (!Number.isFinite(amountCents) || amountCents <= 0) return 'The amount must be greater than $0.';
    if (!Number.isFinite(parsedInstallments) || parsedInstallments < 1 || parsedInstallments > 24) {
      return 'Installments must be between 1 and 24.';
    }
    if (!firstDueOn) return 'A first due date is required.';
    return null;
  }, [ids.length, byStudent, description, amountCents, parsedInstallments, firstDueOn]);

  const input = useMemo(() => {
    if (!schoolYearId || inputError) return null;
    return {
      schoolYearId,
      target,
      ids: [...ids],
      description: description.trim(),
      amountCents,
      installmentCount: parsedInstallments,
      firstDueOn,
    };
  }, [schoolYearId, inputError, target, ids, description, amountCents, parsedInstallments, firstDueOn]);

  // Debounced: an admin typing an amount would otherwise send a preview per
  // keystroke, and each one reads every selected student.
  const previewKey = input ? JSON.stringify(input) : null;
  useEffect(() => {
    if (!open || !previewKey) return undefined;
    const timer = window.setTimeout(() => {
      void runPreview({ variables: { input: JSON.parse(previewKey) } });
    }, 300);
    return () => window.clearTimeout(timer);
  }, [open, previewKey, runPreview]);

  const report = previewKey ? previewData?.bulkInvoicePreview : null;
  const rows = report?.rows ?? [];
  const billed = rows.filter((row) => row.action !== 'skipped');
  const skipped = rows.filter((row) => row.action === 'skipped');

  // A whole school can be a couple of hundred families, and listing every one
  // turns this dialog into a page of scrolling. The counts above the table are
  // the answer to "what am I about to do"; the rows are a spot check.
  const shownBilled = billed.slice(0, PREVIEW_ROW_LIMIT);
  const hiddenBilled = billed.length - shownBilled.length;
  const shownSkipped = skipped.slice(0, SKIPPED_ROW_LIMIT);
  const hiddenSkipped = skipped.length - shownSkipped.length;

  const handleCreate = async () => {
    if (!input) return;
    setError(null);

    const result = await createBulkInvoices({ variables: { input } });
    const payload = result.data?.createBulkInvoices;
    const failure = payload?.userErrors?.[0]?.message;

    if (failure) {
      setError(failure);
      return;
    }

    const created = payload?.report;
    const familyCount = created?.familyCount ?? 0;
    const invoiceCount = created?.invoiceCount ?? 0;
    const createdText = `Created ${invoiceCount} draft ${invoiceCount === 1 ? 'invoice' : 'invoices'} for ${familyCount} ${
      familyCount === 1 ? 'family' : 'families'
    }.`;
    onCompleted(
      invoicesPath ? (
        <>
          {createdText} Post them from the <Link to={invoicesPath} className="underline">Invoices page</Link>.
        </>
      ) : (
        `${createdText} Post them from the Invoices page.`
      )
    );
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create invoices</DialogTitle>
          <DialogDescription>
            One charge, one invoice per family. Charge by student and siblings are billed together on
            their family's invoice, with a line each.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Tabs value={target} onValueChange={(value) => setTarget(value as BulkInvoiceTarget)}>
            <TabsList className="grid w-full max-w-sm grid-cols-2">
              <TabsTrigger value="STUDENTS">By student</TabsTrigger>
              <TabsTrigger value="FAMILIES">By family</TabsTrigger>
            </TabsList>
          </Tabs>

          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <Input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={byStudent ? 'Filter students...' : 'Filter families...'}
                className="max-w-xs"
                aria-label={byStudent ? 'Filter students' : 'Filter families'}
              />
              <Button variant="outline" size="sm" onClick={toggleAllVisible} disabled={visible.length === 0}>
                {allVisibleSelected ? 'Clear' : `Select all (${visible.length})`}
              </Button>
              <span className="text-sm text-muted-foreground">
                {ids.length} selected
              </span>
            </div>

            <div className="max-h-[40vh] overflow-y-auto rounded-md border">
              {visible.length === 0 ? (
                <p className="p-3 text-sm text-muted-foreground">
                  {byStudent ? 'No students in this year.' : 'No families in this year.'}
                </p>
              ) : (
                visible.map((row) => {
                  const checked = ids.includes(row.id);
                  return (
                    <label
                      key={row.id}
                      className="flex cursor-pointer items-center gap-3 border-b px-3 py-2 last:border-b-0 hover:bg-muted/50"
                    >
                      <Checkbox
                        checked={checked}
                        onCheckedChange={(value) => toggleRow(row.id, value === true)}
                        aria-label={row.title}
                      />
                      <span className="font-medium">{row.title}</span>
                      <span className="text-sm text-muted-foreground">{row.detail}</span>
                    </label>
                  );
                })
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="bulk-description">What is this for?</Label>
            <Input
              id="bulk-description"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              placeholder="Soccer fee"
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-2">
              <Label htmlFor="bulk-amount">{byStudent ? 'Amount per student' : 'Amount per family'}</Label>
              <Input
                id="bulk-amount"
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
                inputMode="decimal"
                placeholder="25.00"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="bulk-installments">Installments</Label>
              <Input
                id="bulk-installments"
                value={installmentCount}
                onChange={(event) => {
                  const next = event.target.value;
                  setInstallmentCount(next);
                  const count = parseInt(next, 10);
                  if (Number.isFinite(count)) setFirstDueOn(defaultDueOn(count));
                }}
                inputMode="numeric"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="bulk-first-due">First due date</Label>
              <Input
                id="bulk-first-due"
                type="date"
                value={firstDueOn}
                onChange={(event) => setFirstDueOn(event.target.value)}
              />
            </div>
          </div>

          <p className="text-sm text-muted-foreground">
            {parsedInstallments > 1
              ? 'One invoice a month on this day of the month. Everything is created as a draft.'
              : 'Created as a draft.'}{' '}
            Parents pay these themselves; a charge made here is never collected automatically.
          </p>

          {inputError && ids.length > 0 && (
            <p className="text-sm text-muted-foreground">{inputError}</p>
          )}

          {error && (
            <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {previewing && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Working out who gets billed...
            </div>
          )}

          {report && !previewing && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm font-medium">
                <Users className="h-4 w-4" />
                {report.familyCount} {report.familyCount === 1 ? 'family' : 'families'} · {report.invoiceCount}{' '}
                {report.invoiceCount === 1 ? 'invoice' : 'invoices'} · {formatCurrency(report.totalCents)}
              </div>

              {billed.length > 0 && (
                <div className="overflow-x-auto rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Family</TableHead>
                        {byStudent && <TableHead>Students</TableHead>}
                        <TableHead className="text-right">Invoices</TableHead>
                        <TableHead className="text-right">Total</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {shownBilled.map((row) => (
                        <TableRow key={row.familyEnrollmentId}>
                          <TableCell className="font-medium">{row.familyName}</TableCell>
                          {byStudent && (
                            <TableCell className="text-sm text-muted-foreground">
                              {row.studentNames.join(', ')}
                            </TableCell>
                          )}
                          <TableCell className="text-right">{row.invoiceCount}</TableCell>
                          <TableCell className="text-right">{formatCurrency(row.totalCents)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {hiddenBilled > 0 && (
                    <p className="border-t px-3 py-2 text-sm text-muted-foreground">
                      and {hiddenBilled} more {hiddenBilled === 1 ? 'family' : 'families'}
                    </p>
                  )}
                </div>
              )}

              {report.invoiceCount >= SLOW_WRITE_INVOICES && (
                <p className="text-sm text-amber-700">
                  {report.invoiceCount} invoices takes a few seconds to create. Leave this open until it finishes.
                </p>
              )}

              {skipped.length > 0 && (
                <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                  <p className="font-medium">
                    {skipped.length} {skipped.length === 1 ? 'selection' : 'selections'} cannot be billed
                  </p>
                  <ul className="mt-1 list-disc pl-5">
                    {shownSkipped.map((row, index) => (
                      <li key={`${row.message}-${index}`}>{row.message}</li>
                    ))}
                    {hiddenSkipped > 0 && <li>and {hiddenSkipped} more</li>}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={creating}>
            Cancel
          </Button>
          <Button onClick={handleCreate} disabled={!input || creating || previewing || billed.length === 0}>
            {creating
              ? 'Creating...'
              : `Create ${report?.invoiceCount ?? 0} ${report?.invoiceCount === 1 ? 'draft' : 'drafts'}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default BulkInvoiceDialog;
